/**
 * Lake Agent API — per-user lake conversation history + thread management.
 *
 * Mirrors `services/api/agent.ts` but talks to the v1 lake-agent endpoints
 * and is not scoped to a case. Threads here are global per user.
 */

import type { LangChainMessage } from '@assistant-ui/react-langgraph'
import type { AgentOptions, AgentThreadHealth, AgentToolInfo } from '@/services/api/agent'
import { ApiError, apiClient } from '@/services/api/client'

export interface LakeAgentHistoryResponse {
  messages: LangChainMessage[]
  thread_id: string | null
  next_cursor?: string | null
  has_more?: boolean
}

export interface LakeAgentThreadItem {
  id: string
  title: string | null
  created_at: string
  updated_at: string
}

const BASE = '/v1/lake-agent'

export async function getLakeAgentHistory(
  threadId?: string,
  opts?: { limit?: number; before?: string }
): Promise<LakeAgentHistoryResponse> {
  if (!threadId) {
    // No thread yet — return an empty envelope so the hook short-circuits
    // without firing a no-op network call.
    return { messages: [], thread_id: null, has_more: false, next_cursor: null }
  }
  const params: Record<string, string | number> = {}
  if (opts?.limit) params.limit = opts.limit
  if (opts?.before) params.before = opts.before
  try {
    return await apiClient.get<LakeAgentHistoryResponse>(`${BASE}/threads/${threadId}/history`, {
      params,
    })
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      return { messages: [], thread_id: null }
    }
    throw err
  }
}

export async function getLakeThreadHealth(threadId: string): Promise<AgentThreadHealth> {
  return apiClient.get(`${BASE}/threads/${threadId}/health`)
}

export async function listLakeThreads(): Promise<LakeAgentThreadItem[]> {
  return apiClient.get(`${BASE}/threads`)
}

export async function createLakeThread(): Promise<LakeAgentThreadItem> {
  return apiClient.post(`${BASE}/threads`)
}

export async function deleteLakeThread(threadId: string): Promise<void> {
  await apiClient.delete(`${BASE}/threads/${threadId}`)
}

export async function renameLakeThread(
  threadId: string,
  title: string
): Promise<LakeAgentThreadItem> {
  return apiClient.patch(`${BASE}/threads/${threadId}`, { title })
}

export async function getLakeAgentTools(): Promise<{ tools: AgentToolInfo[] }> {
  return apiClient.get(`${BASE}/tools`)
}

export async function updateLakeThreadOptions(
  threadId: string,
  options: AgentOptions
): Promise<LakeAgentThreadItem> {
  return apiClient.patch(`${BASE}/threads/${threadId}`, { agent_options: options })
}

export const lakeAgentApi = {
  getLakeAgentHistory,
  getLakeThreadHealth,
  listLakeThreads,
  createLakeThread,
  deleteLakeThread,
  renameLakeThread,
}
